// Product service for API calls
import {PaginatedProductResponse, Product, RawProduct} from '../types';
import {fetchWithErrorHandling, guestPrefix} from './apiUtils';

export function transformRawProduct(raw: RawProduct): Product {
  return {
    id: raw.id,
    name: raw.item,
    description: raw.description || '',
    shortDescription: raw.shortDescription,
    images: raw.images || [],
    printImages: raw.printOnDemand?.images ? {
      frontForReview: raw.printOnDemand.images.frontForReview ?? null,
    } : undefined,
    departments: raw.departments || [],
    categories: raw.categories || [],
    variations: (raw.variations || []).map(v => ({
      id: v.id,
      size: v.size,
      color: v.color,
      hex: v.hex,
      price: v.price,
      stock: v.stock,
      images: v.images,
      description: v.description,
    })),
    basePrice: raw.price,
    isPrintDetail: raw.isPrintDetail,
    printDetailType: raw.printOnDemand?.type,
    printOnDemandStyle: raw.printOnDemand?.style,
    useCardBuilder: raw.printOnDemand?.useCardBuilder ?? false,
    cardTemplateId: raw.printOnDemand?.cardTemplateId ?? null,
    printOnDemandFields: raw.printOnDemand?.fields,
    printOnDemandOptions: raw.printOnDemand?.options,
    stock: raw.stock,
    allowBackorder: raw.allowBackorder,
    allowOnDemand: raw.allowOnDemand,
    promoCodeOnly: raw.promoCodeOnly,
    minOrderQty: raw.minOrderQty,
    maxOrderQty: raw.maxOrderQty,
    productionTimeline: raw.productionTimeline,
    customerCharges: raw.customerCharges || [],
    quantityDiscounts: raw.quantityDiscounts || [],
    personalizationEnabled: !!raw.personalization,
    personalizationInstructions: raw.personalization?.instructions ?? null,
    personalizationFee: raw.personalization?.fee != null ? parseFloat(raw.personalization.fee) : null,
    personalizationCharLimit: raw.personalization?.charLimit ?? null,
    personalizationPlacements: Array.isArray(raw.personalization?.placements) ? raw.personalization.placements : [],
  };
}

export class ProductService {
  static async getProducts(page: number = 1, departmentId?: number, categoryId?: number, search?: string, sort?: string, size?: number, signal?: AbortSignal): Promise<PaginatedProductResponse> {
    try {
      if (!size) {
        const storedPerPage = parseInt(localStorage.getItem('perPage') || '', 10);
        size = storedPerPage > 0 ? storedPerPage : undefined;
      }
          
      // Build query string with filters
      const params = new URLSearchParams({ page: page.toString() });
      if (departmentId) {
        params.append('department', departmentId.toString());
      }
      if (categoryId) {
        params.append('category', categoryId.toString());
      }
      if (search) {
        params.append('search', search);
      }
      if (sort) {
        params.append('sort', sort);
      }
      if (size) {
        params.append('size', size.toString());
      }

      // Try API first - use guest or authenticated endpoint based on auth state
      const result = await fetchWithErrorHandling<{
        data: RawProduct[];
        total: number;
        size: number;
        currentPage: number;
        totalPages: number;
      }>(`/api/v2${guestPrefix()}/products?${params.toString()}`, signal ? { signal } : undefined);

      // Transform raw products to Product format
      return {
        ...result,
        data: result.data.map(transformRawProduct)
      };
    } catch {
      //TODO: Remove this - incorrect approach, must be fixed.
      // Fall back to local JSON file
      try {
        const { default: localProducts } = await import('../data/products.json');
        let products = (localProducts as RawProduct[]).map(transformRawProduct);

        // Apply client-side filtering in fallback mode
        if (departmentId) {
          products = products.filter(product =>
            product.departments.includes(departmentId)
          );
        }
        if (categoryId) {
          products = products.filter(product =>
            product.categories.includes(categoryId)
          );
        }
        if (search) {
          const searchLower = search.toLowerCase();
          products = products.filter(product =>
            product.name.toLowerCase().includes(searchLower) ||
            product.description.toLowerCase().includes(searchLower)
          );
        }

        return {
          data: products,
          total: products.length,
          size: products.length,
          currentPage: 1,
          totalPages: 1
        };
      } catch (localError) {
        console.error('Failed to load products from both API and local data:', localError);
        throw new Error('Unable to load products from both API and local data.');
      }
    }
  }

  static async getProduct(id: number, signal?: AbortSignal): Promise<Product> {
    const raw = await fetchWithErrorHandling<RawProduct>(`/api/v2${guestPrefix()}/products/${id}`, signal ? { signal } : undefined);
    return transformRawProduct(raw);
  }

  static async searchProducts(query: string): Promise<Product[]> {
    try {
      const encodedQuery = encodeURIComponent(query);
      const rawProducts = await fetchWithErrorHandling<RawProduct[]>(`/api/v2${guestPrefix()}/products/search?q=${encodedQuery}`, undefined);
      return rawProducts.map(transformRawProduct);
    } catch (error) {
      console.error('Failed to search products:', error);

      // Fallback to client-side search
      try {
        const response = await this.getProducts();
        return response.data.filter(product => {
          const nameMatch = product.name.toLowerCase().includes(query.toLowerCase());
          const descriptionMatch = product.description.toLowerCase().includes(query.toLowerCase());

          return nameMatch || descriptionMatch;
        });
      } catch (fallbackError) {
        console.error('Fallback search also failed:', fallbackError);
        return [];
      }
    }
  }
}
